/* ==========================================================================
   Cart add-on (shipping protection) — replaces the UpCart add-on module.

   Keeps one toggle row in step with the cart:
     - switched on by default (configurable) once the cart has real items;
     - switching it off removes the line and records the choice as a cart
       attribute, so it is not silently re-added on the next page;
     - the row hides itself while the cart holds nothing else.
   ========================================================================== */

(function () {
  'use strict';

  var OPT_OUT = 'shipping_protection_declined';

  // The row renders in BOTH the drawer and the cart page, so two instances can
  // be live at once. Cart writes share one lock, or they would both decide the
  // add-on is missing and each add it.
  var LOCK = { busy: false };

  function cartRoute(name, fallback) {
    return (window.theme && theme.routes && theme.routes[name]) || fallback;
  }

  function getCart() {
    return fetch(cartRoute('cart', '/cart.js'), { credentials: 'same-origin' })
      .then(function (r) { return r.json(); });
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.json(); });
  }

  class CartAddon {
    constructor(el) {
      this.el = el;
      this.variantId = Number(el.dataset.variantId);
      this.autoAdd = el.dataset.autoAdd === 'true';
      this.toggle = el.querySelector('[data-cart-addon-toggle]');

      this.toggle.addEventListener('click', this.onToggle.bind(this));

      document.addEventListener('cart:updated', function (event) {
        var cart = event.detail && event.detail.cart;
        if (cart) this.sync(cart);
        else this.refresh();
      }.bind(this));

      // The theme fires this after its own add-to-cart, before cart:updated.
      document.addEventListener('ajaxProduct:added', this.refresh.bind(this));

      this.refresh();
    }

    refresh() {
      if (LOCK.busy) return Promise.resolve();
      return getCart().then(this.sync.bind(this)).catch(function () {});
    }

    /* Lines that are not the add-on itself. */
    otherItems(cart) {
      var id = this.variantId;
      return cart.items.filter(function (i) { return i.variant_id !== id; });
    }

    addonLine(cart) {
      var id = this.variantId;
      return cart.items.find(function (i) { return i.variant_id === id; }) || null;
    }

    declined(cart) {
      return (cart.attributes || {})[OPT_OUT] === 'yes';
    }

    sync(cart) {
      if (LOCK.busy) return;

      var line = this.addonLine(cart);
      var others = this.otherItems(cart);

      // Nothing else in the cart: hide the row, and never leave the add-on alone.
      if (others.length === 0) {
        this.el.hidden = true;
        this.updateCount(others);
        this.updateSubtotalWas(cart);
        if (line) this.remove(line);
        return;
      }

      this.el.hidden = false;
      this.setChecked(!!line);
      this.updateCount(others);
      this.updateSubtotalWas(cart);

      if (!line && this.autoAdd && !this.declined(cart)) {
        this.add();
      }
    }

    /* The drawer heading count, excluding the add-on itself. */
    updateCount(others) {
      var n = others.reduce(function (a, i) { return a + i.quantity; }, 0);
      document.querySelectorAll('[data-cart-drawer-count]').forEach(function (el) {
        el.textContent = n > 0 ? ' • ' + n : '';
      });
    }

    /* The pre-discount cart total, struck through in front of the subtotal.
       Uses the WHOLE cart's original total so that
       struck - discounts = subtotal, add-on included. */
    updateSubtotalWas(cart) {
      var els = document.querySelectorAll('[data-subtotal-was]');
      if (!els.length) return;

      var was = cart.original_total_price;
      var now = cart.total_price;
      var show = was > now && cart.item_count > 0;
      var text = '';

      if (show && window.theme && theme.Currency && theme.Currency.formatMoney) {
        text = theme.Currency.formatMoney(was, theme.moneyFormat);
      } else if (show) {
        text = (was / 100).toFixed(2);
      }

      els.forEach(function (el) {
        el.textContent = text;
        el.hidden = !show;
      });
    }

    setChecked(on) {
      this.toggle.setAttribute('aria-checked', on ? 'true' : 'false');
      this.el.classList.toggle('is-on', on);
    }

    onToggle() {
      if (LOCK.busy) return;
      var on = this.toggle.getAttribute('aria-checked') === 'true';

      if (on) {
        getCart().then(function (cart) {
          var line = this.addonLine(cart);
          if (line) this.remove(line, true);
        }.bind(this));
      } else {
        this.add(true);
      }
    }

    add(clearOptOut) {
      LOCK.busy = true;
      this.el.classList.add('is-busy');

      var chain = postJSON(cartRoute('cartAdd', '/cart/add.js'), {
        items: [{ id: this.variantId, quantity: 1 }]
      });

      if (clearOptOut) {
        chain = chain.then(function () {
          return postJSON('/cart/update.js', { attributes: attrs(OPT_OUT, '') });
        });
      }

      return chain.then(this.done.bind(this, true)).catch(this.done.bind(this, false));
    }

    remove(line, recordOptOut) {
      LOCK.busy = true;
      this.el.classList.add('is-busy');

      var chain = postJSON(cartRoute('cartChange', '/cart/change.js'), {
        id: line.key,
        quantity: 0
      });

      if (recordOptOut) {
        chain = chain.then(function () {
          return postJSON('/cart/update.js', { attributes: attrs(OPT_OUT, 'yes') });
        });
      }

      return chain.then(this.done.bind(this, true)).catch(this.done.bind(this, false));
    }

    done(ok) {
      LOCK.busy = false;
      this.el.classList.remove('is-busy');
      // Let the theme redraw the line items and subtotal, then re-read state.
      document.dispatchEvent(new CustomEvent('cart:build'));
      return getCart().then(this.sync.bind(this)).catch(function () {});
    }
  }

  function attrs(key, value) {
    var o = {};
    o[key] = value;
    return o;
  }

  function init() {
    document.querySelectorAll('[data-cart-addon]').forEach(function (el) {
      if (el.dataset.addonReady) return;
      el.dataset.addonReady = '1';
      new CartAddon(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
